import { Limitation, Requirement, Reward, StoreRequirement } from '../../types/discounts';
import { Campaign, DiscountItem, DiscountType, GroupItems, LoyaltyType, LoyaltyDiscountType } from '../types';
import { FormikErrors, FormikTouched } from 'formik';
import { ValidationState } from '../../components/Forms';

interface Item {
    id: string;
    group_id?: string;
}

export const getLimitationByDiscountType = (discount: DiscountItem): Limitation => {
    const blacklistedItems = discount.items
        .filter((item) => item.isBlacklisted)
        .map((item) => ({ id: item.id, group_id: item?.groupId }));

    switch (discount.type) {
        case DiscountType.loyalty:
            return getLoyaltyDiscountLimitation(discount, blacklistedItems);
        case DiscountType.discountInNok || DiscountType.discountInAmount:
            return {
                discount_combination: discount.discountCombination,
                discount_reward_usage: 1,
                discount_repeat_usage: !!discount.discountUsageAmount ? Number(discount.discountUsageAmount) : -1,
                blacklist: blacklistedItems.length > 0 ? blacklistedItems : undefined,
            };
        default:
            return {
                discount_combination: discount.discountCombination,
                discount_repeat_usage: !!discount.discountUsageAmount ? Number(discount.discountUsageAmount) : -1,
                blacklist: blacklistedItems.length > 0 ? blacklistedItems : undefined,
            };
    }
};

const getLoyaltyDiscountLimitation = (discount: DiscountItem, blacklistedItems: Item[]): Limitation => {
    const daysInMonth = 30;
    const expireDays = Number(discount.validFor) * daysInMonth;
    const discountHours = discount.requirements
        ? {
            hours: discount.requirements.map((requirement) => ({
                day: requirement.day as 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun',
                start: `${requirement.timeFrom.getHours}:${requirement.timeFrom.getMinutes}`,
                end: `${requirement.timeTo.getHours}:${requirement.timeTo.getMinutes}`,
            })),
        }
        : undefined;

    switch (discount.loyaltyType) {
        case LoyaltyType.productsBought:
            return {
                discount_combination: discount.discountCombination,
                stamp_expire_days: expireDays,
                blacklist: blacklistedItems.length > 0 ? blacklistedItems : undefined,
            };
        case LoyaltyType.visitAndSpending:
            return {
                discount_combination: discount.discountCombination,
                stamp_expire_days: expireDays,
                blacklist: blacklistedItems.length > 0 ? blacklistedItems : undefined,
                discount_hours: discountHours,
            };
    }
};

const getRequirementStore = (campaign: Campaign): StoreRequirement | undefined => {
    if (campaign.mccs || campaign.mccRanges || campaign.storeIds) {
        const storeRequirement: StoreRequirement = {};
        if (campaign.mccs) {
            storeRequirement.mcc = campaign.mccs;
        }
        if (campaign.mccRanges) {
            storeRequirement['mcc_ranges'] = campaign.mccRanges.map(([start, end]) => ({ end, start }));
        }
        if (campaign.storeIds) {
            storeRequirement.id = campaign.storeIds;
        }
        return storeRequirement;
    }
};

export const getRequirementsByDiscountType = (discount: DiscountItem, campaign: Campaign): Requirement => {
    const fromDate = campaign.dateFrom;
    fromDate.setHours(campaign.timeFrom.getHours(), campaign.timeFrom.getMinutes());
    const toDate = campaign.dateTo;
    toDate.setHours(campaign.timeTo.getHours(), campaign.timeTo.getMinutes());
    const items = discount.items
        .filter((item) => !item.isBlacklisted)
        .map((item) => {
            if (!!item.id) return { id: item.id };
            else if (!!item.groupId) return { group_id: item.groupId };
            else return {};
        });

    switch (discount.type) {
        case DiscountType.discountInNok:
            return {
                gross_amount: Number(discount.totalPurchase) * 100,
                item: {
                    items: items.length > 0 && discount.groupItems !== GroupItems.allItems ? items : undefined,
                },
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
                store_ids: campaign.storeIds,
                store: getRequirementStore(campaign),
            };
        case DiscountType.discountInAmount:
            return {
                gross_amount: Number(discount.totalPurchase) * 100,
                item: {
                    items: items.length > 0 && discount.groupItems !== GroupItems.allItems ? items : undefined,
                },
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
                store_ids: campaign.storeIds,
                store: getRequirementStore(campaign),
            };
        case DiscountType.discountInPercentage:
            return {
                item: {
                    items: items.length > 0 && discount.groupItems !== GroupItems.allItems ? items : undefined,
                },
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
                store_ids: campaign.storeIds,
                store: getRequirementStore(campaign),
            };
        case DiscountType.amountBuyDiscount:
            return {
                item: {
                    items: items.length > 0 && discount.groupItems !== GroupItems.allItems ? items : undefined,
                    quantity: Number(discount.discountValue),
                },
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
                store_ids: campaign.storeIds,
                store: getRequirementStore(campaign),
            };
        case DiscountType.fixedPriceDiscount:
            return {
                item: {
                    items: items.length > 0 && discount.groupItems !== GroupItems.allItems ? items : undefined,
                },
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
                store_ids: campaign.storeIds,
                store: getRequirementStore(campaign),
            };
        case DiscountType.loyalty:
            return getLoyaltyDiscountRequirement(items, fromDate, toDate, discount);
    }
};

export const getLoyaltyDiscountRequirement = (
    items: any,
    fromDate: Date,
    toDate: Date,
    discount: DiscountItem
): Requirement => {
    switch (discount.loyaltyType) {
        case LoyaltyType.productsBought:
            return {
                item: {
                    items: items.length > 0 ? items : undefined,
                },
                stamp: Number(discount.itemsBought),
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
            };
        case LoyaltyType.visitAndSpending:
            return {
                item: {
                    items: items.length > 0 ? items : undefined,
                },
                gross_amount: Number(discount.minimumPurchase) * 100,
                stamp: Number(discount.visitsNumber),
                purchase_from: fromDate.toISOString(),
                purchase_to: toDate.toISOString(),
            };
    }
};

export const getRewardByDiscountType = (discount: DiscountItem): Reward => {
    switch (discount.type) {
        case DiscountType.discountInNok:
            return {
                type: 'discount_amount',
                value: Number(discount.discountValue) * 100,
            };
        case DiscountType.discountInAmount:
            return {
                type: 'discount_amount',
                value: Number(discount.discountValue) * 100,
            };
        case DiscountType.discountInPercentage:
            return {
                type: 'discount_percent',
                value: Number(discount.discountValue),
            };
        case DiscountType.amountBuyDiscount:
            return {
                type: 'discount_item_quantity',
                value: Number(discount.discountValue) - Number(discount.payFor),
            };
        case DiscountType.fixedPriceDiscount:
            return {
                type: 'discount_item_new_price',
                value: Number(discount.discountValue) * 100,
            };
        case DiscountType.loyalty:
            switch (discount.loyaltyDiscountType) {
                case LoyaltyDiscountType.inPercentage:
                    return {
                        type: 'discount_item_percent',
                        value: Number(discount.discountValue),
                    };
                case LoyaltyDiscountType.inCurrency:
                    return {
                        type: 'discount_item_new_price',
                        value: Number(discount.discountValue) * 100,
                    };
                case LoyaltyDiscountType.freeItem:
                    return {
                        type: 'discount_item_percent',
                        value: 100,
                    };
            }
    }
};

export const validateNested = (index: number, errors: FormikErrors<Campaign>, touched: FormikTouched<Campaign>) => (
    fieldName: string
) => {
    if (touched.discounts && touched.discounts[index] && errors.discounts && errors.discounts[index]) {
        const fieldsErrors = errors.discounts[index] as any;
        const fieldsTouched = touched.discounts[index] as any;
        return fieldsTouched[fieldName] && fieldsErrors[fieldName]
            ? {
                state: ValidationState.Invalid,
                message: fieldsErrors[fieldName],
            }
            : undefined;
    }
    return undefined;
};
